import type {ReactiveController, ReactiveControllerHost} from "lit";

/** What a host gives the frame loop. */
export interface FrameLoopOptions {
    /** Whether the host wants frames (its clock runs). */
    readonly running: () => boolean;
    /** Called on every frame — typically moves the host's "now". */
    readonly onFrame: () => void;
    /** The page became visible again (the timer checks for an end it missed). */
    readonly onVisible?: () => void;
    /** The host's one frame slot (`cb => this.requestFrame(cb)` of `AbstractO2dElement`, which also cancels the
     *  pending frame on disconnect — `requestFrame` is protected, hence injected). */
    readonly schedule: (callback: () => void) => void;
}

export interface FrameLoopHost extends ReactiveControllerHost {
    readonly isConnected: boolean;
}

/**
 * The frame loop of the clock gimmicks (feature plans 0071, 0073): frames only while the host runs, is connected
 * and the page is visible. The base class offers no way to cancel a frame other than on disconnect, so stopping
 * clears `_looping` and the one pending frame ends the loop — a paused or hidden clock costs at most that one
 * frame. `_looping` also keeps `sync()` from requesting the pending frame again on every call.
 *
 * Call `sync()` after every change of the host's state; visibility and connection are watched here.
 */
export class FrameLoopController implements ReactiveController {

    private _looping = false;

    public constructor(private readonly host: FrameLoopHost, private readonly options: FrameLoopOptions) {
        host.addController(this);
    }

    public hostConnected(): void {
        document.addEventListener('visibilitychange', this._onVisibilityChange);
    }

    public hostDisconnected(): void {
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this._looping = false;
    }

    /** Starts the loop when it is wanted and not running; marks it stopped when it is not wanted. */
    public sync(): void {
        const wanted = this._wanted();
        if (wanted && !this._looping) {
            this._looping = true;
            this.options.schedule(this._tick);
        } else if (!wanted) {
            this._looping = false;
        }
    }

    private _wanted(): boolean {
        return this.host.isConnected && this.options.running() && document.visibilityState !== 'hidden';
    }

    private readonly _tick = (): void => {
        this.options.onFrame();
        if (this._wanted()) {
            this.options.schedule(this._tick);
        } else {
            this._looping = false;
        }
    };

    private readonly _onVisibilityChange = (): void => {
        if (document.visibilityState !== 'hidden') {
            this.options.onVisible?.();
        }
        this.sync();
    };
}
